import React from 'react';
import { DiffStats } from '../../types';
import {
  ChevronUp,
  ChevronDown,
  FoldVertical,
  UnfoldVertical,
  Space,
} from 'lucide-react';

interface DiffToolbarProps {
  totalHunks: number;
  activeHunkIndex: number;
  onNextHunk: () => void;
  onPrevHunk: () => void;
  foldUnchanged: boolean;
  onToggleFold: () => void;
  ignoreWhitespace: boolean;
  onToggleWhitespace: () => void;
  stats?: DiffStats;
}

export const DiffToolbar: React.FC<DiffToolbarProps> = ({
  totalHunks,
  activeHunkIndex,
  onNextHunk,
  onPrevHunk,
  foldUnchanged,
  onToggleFold,
  ignoreWhitespace,
  onToggleWhitespace,
  stats,
}) => {
  const linesAdded = stats?.linesAdded || stats?.lines_added || 0;
  const linesRemoved = stats?.linesRemoved || stats?.lines_removed || 0;
  const linesModified = stats?.linesModified || stats?.lines_modified || 0;

  return (
    <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs flex flex-wrap items-center justify-between gap-3 text-xs">
      {/* Diff Metrics */}
      <div className="flex items-center gap-3 font-mono">
        <span className="font-semibold text-slate-700 dark:text-slate-300">Diff Stats:</span>
        <span className="text-emerald-600 dark:text-emerald-400 font-bold">
          +{linesAdded} additions
        </span>
        <span className="text-rose-600 dark:text-rose-400 font-bold">
          -{linesRemoved} deletions
        </span>
        {linesModified > 0 && (
          <span className="text-indigo-600 dark:text-indigo-400 font-medium">
            ~{linesModified} modifications
          </span>
        )}
      </div>

      {/* Navigation & Controls */}
      <div className="flex items-center gap-2">
        {/* Hunk Navigator */}
        {totalHunks > 0 && (
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
            <span className="px-2 text-[11px] text-slate-600 dark:text-slate-300 font-medium">
              Hunk {activeHunkIndex + 1} of {totalHunks}
            </span>
            <button
              onClick={onPrevHunk}
              className="p-1 rounded hover:bg-white dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300"
              title="Previous change hunk"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onNextHunk}
              className="p-1 rounded hover:bg-white dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300"
              title="Next change hunk"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Code Folding Toggle */}
        <button
          onClick={onToggleFold}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border font-medium transition-colors ${
            foldUnchanged
              ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700'
          }`}
          title="Fold or expand unchanged context lines"
        >
          {foldUnchanged ? <FoldVertical className="w-3.5 h-3.5" /> : <UnfoldVertical className="w-3.5 h-3.5" />}
          {foldUnchanged ? 'Folded' : 'Expanded'}
        </button>

        {/* Ignore Whitespace Toggle */}
        <button
          onClick={onToggleWhitespace}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border font-medium transition-colors ${
            ignoreWhitespace
              ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700'
          }`}
          title="Ignore leading/trailing whitespace changes"
        >
          <Space className="w-3.5 h-3.5" />
          Ignore Whitespace
        </button>
      </div>
    </div>
  );
};
